import styled from "@emotion/styled"
import { useEffect, useLayoutEffect, useState, useRef } from "react"
import { Helmet } from "react-helmet-async"
import { useConsole } from "contexts/Console"
import { useLocale } from "contexts/Locale"
import { useUser } from "contexts/User"
import getMediaQuery from "css/breakpoints"
import { fullGrid } from "css/grid"
import { bold, legend100 } from "css/text"
import Button from "components/button/Button"
import LangDropDown from "./LangDropDown"
import { cssVars, useNav } from "components/nav-v2/Wrapper"
import { animate } from "framer-motion"

const Root = styled.div`
  position: sticky;
  inset-block-start: 0;
  z-index: 10000;

  [data-cfg="true"] & {
    z-index: -1;
  }

  transform: translate3d(
    0,
    calc((1 - min(var(${cssVars.main.alert.expanded}), var(${cssVars.main.bar.pinned})) + var(${cssVars.main.panes.expanded})) * -100%),
    0
  );

  &[data-actions*="lock"] {
    ${cssVars.main.alert.expanded}: var(${cssVars.main.lock});
  }

  ${getMediaQuery("m", { max: false })} {
    transform: translate3d(0, calc((1 - min(var(${cssVars.main.alert.expanded}), var(${cssVars.main.bar.pinned}))) * -100%), 0);

    body:has([data-actions*="conceal"]) & {
      z-index: 9996;
    }

    body:has([data-actions*="conceal"]:not([data-actions*="safe"])) & {
      transform: translate3d(0, calc(var(${cssVars.main.panes.expanded}) * -100%), 0);
    }
  }

  width: 100%;
  height: var(${cssVars.main.alert.height});
  background: rgb(var(--light-grey));
  color: rgb(var(--light-black));

  ${fullGrid}
  align-items: center;
  grid-template-rows: 10px 1fr 1fr;

  ${getMediaQuery("m")} {
    grid-template-rows: 1fr;
  }
`

const Para = styled.p`
  grid-column: 2 / span 6;
  grid-row: 2;
  ${legend100};
  align-self: end;
  margin-block-end: 5px;
  color: rgb(var(--light-black));
  ${getMediaQuery("m")} {
    margin-block-end: unset;
    align-self: center;
    grid-row: 1;
    grid-column: 2 / span 4;
  }

  > span {
    ${bold}
  }
`

const Close = styled(Button)`
  display: none !important;
  --btn-height: 2rem;
  grid-column: col 6;
  margin-inline-end: calc(var(--btn-height) / -2 + var(--grid-gap) / -2);
  grid-row: 2;
  align-self: start;
  justify-self: end;
  > svg {
    width: 10px !important;
    height: 10px !important;
  }

  ${getMediaQuery("m")} {
    margin-inline-end: unset;
    grid-row: 1;
    grid-column: col 13;
    justify-self: center;
    align-self: center;
  }
`

export default function Banner({ reason }) {
  const console = useConsole()
  const rbar = useRef(null)
  const nav = useNav()
  const { banners, codes, locales, current, ...locale } = useLocale()
  const user = useUser()
  const isBanSess = process.browser && (sessionStorage.getItem("curban") === "true" || sessionStorage.getItem("curban") === null)
  const [isVisible, setIsVisible] = useState(!!user.displayCurrencyBanner && isBanSess)

  const onClose = () => {
    sessionStorage.setItem("curban", "false")
    setIsVisible(false)
    document.body.classList.remove("curban")
  }

  let navLocale = locale.defaultLocale
  for (const language of user.languages.map(code => code.toLowerCase())) {
    if (codes.includes(language)) {
      navLocale = locales.find(locale => locale.codes.www === language)
      break
    }
  }

  const alertText = banners[navLocale.banner].generic
  const ctaText = banners[navLocale.banner].cta

  let targetLocale
  let filteredLocales
  for (const intermediaryLanguage of user.languages.map(code => code.toLowerCase())) {
    const candidateLocale = intermediaryLanguage.toLowerCase()
    const [language, intermediaryCountry] = candidateLocale.split("-")
    const country = (intermediaryCountry ?? "").toUpperCase()

    if (country === user.realCountryCode && codes.includes(candidateLocale)) {
      targetLocale = locales.find(locale => locale.codes.www === candidateLocale)
      break
    }

    if (!country) {
      const candidateLocale = `${language}-${user.realCountryCode}`.toLowerCase()
      if (codes.includes(candidateLocale)) {
        targetLocale = locales.find(locale => locale.codes.www === candidateLocale)
        break
      }
    }
  }
  if (targetLocale) {
    filteredLocales = [...locales.filter(locale => locale.codes.country === targetLocale.codes.country)]
  }

  if (!targetLocale) {
    for (const candidateLanguage of user.languages) {
      const [language, country] = candidateLanguage.split("-")

      if (codes.includes(language)) {
        targetLocale = locales.find(locale => locale.codes.www === language)
        break
      }
    }
    targetLocale ||= locale.defaultLocale
    filteredLocales = [targetLocale]
  }

  useEffect(() => {
    const isCurSess = sessionStorage.getItem("curban")
    if (isCurSess === null) {
      sessionStorage.setItem("curban", "true")
    } else if (isCurSess === "false") {
      setIsVisible(false)
    }
  }, [])

  useLayoutEffect(
    function syncCSSWithState() {
      const current = nav.main.alert.motion.expanded.get()
      if (Number.isInteger(current) && current !== +isVisible) {
        animate(nav.main.alert.motion.expanded, +isVisible)
      }
    },
    [isVisible]
  )

  useLayoutEffect(function syncWithNavLock() {
    const bar = rbar.current
    const controller = new AbortController()

    nav.addEventListener(
      "main:lock",
      ({ t }) => {
        bar.dataset.actions = "lock"
      },
      { signal: controller.signal }
    )

    nav.addEventListener(
      "main:unlock",
      () => {
        bar.dataset.actions = ""
      },
      { signal: controller.signal }
    )
    return () => controller.abort()
  })

  console.verbose("CurrencyBanner()")
  return isVisible ? (
    <>
      <Helmet>
        <body className='curban' />
      </Helmet>
      <Root className='cbar dark-theme' lang={navLocale.codes.ws} dir={navLocale.dir}>
        <Para id='languageBannerText' dangerouslySetInnerHTML={{ __html: alertText }} />
        <LangDropDown onClose={onClose} cta={ctaText} targetLocale={targetLocale} filteredLocales={filteredLocales} />
      </Root>
    </>
  ) : null
}
